// A brand's content, as the browser reads it. The schemas that check it are in contentSchema.ts; only
// their types are used here, so this stays free of zod.
import type { ContentPage, Site } from "./contentSchema";
export type { ContentLink, ContentPage, Section, Site } from "./contentSchema";

/** `[label](href)` in text; the client's RichText renders the same pattern. */
export const TEXT_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;

export interface BrandContent {
  site: Site;
  /** By path, in file name order. */
  pages: ContentPage[];
}

/** The internal paths a piece of content links to, to check them against the site's routes. */
export function contentLinks({ site, pages }: BrandContent): string[] {
  const links: string[] = [];
  for (const item of site.nav) {
    links.push(item.to);
    if (Array.isArray(item.children)) links.push(...item.children.map((c) => c.to));
  }
  for (const col of site.footer.columns) if ("links" in col) links.push(...col.links.map((l) => l.to));
  const markdownLinks = (text?: string) => [...(text ?? "").matchAll(TEXT_LINK)].map((m) => m[2]!);
  links.push(...markdownLinks(site.footer.blurb));
  for (const page of pages) {
    for (const s of page.sections) {
      for (const value of Object.values(s)) {
        const values = Array.isArray(value) ? value : [value];
        for (const v of values) {
          if (typeof v === "string") links.push(...markdownLinks(v));
          else if (v && typeof v === "object") {
            if ("to" in v && typeof v.to === "string") links.push(v.to);
            for (const inner of Object.values(v)) if (typeof inner === "string") links.push(...markdownLinks(inner));
          }
        }
      }
    }
  }
  return links.filter((l) => l.startsWith("/")).map((l) => l.split(/[?#]/)[0]!);
}
